import React, { useEffect, useState, useCallback } from 'react';
import { ClipboardList } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { schoolApi } from '../../api/school';
import { PageHeader, Card, EmptyState, LoadingState } from '../../components/school/ui';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const KIND_COLORS = { invoice: 'bg-blue-100 text-blue-700', receipt: 'bg-emerald-100 text-emerald-700', waiver: 'bg-amber-100 text-amber-700' };

export default function AccountingJournalPage() {
  const orgId = useOrgId();
  const { selectedYearId } = useAcademicYear();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ date_from: '', date_to: '', entry_kind: '', class_id: '' });

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const params = { academic_year_id: selectedYearId };
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      if (f.entry_kind) params.entry_kind = f.entry_kind;
      if (f.class_id) params.class_id = f.class_id;
      setRows(await schoolApi.financeJournal(orgId, params));
    } catch { toast.error('Failed to load journal'); }
    finally { setLoading(false); }
  }, [orgId, selectedYearId, f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (orgId) schoolApi.listClasses(orgId, selectedYearId).then(setClasses).catch(() => {}); }, [orgId, selectedYearId]);

  const money = (v) => (Number(v) || 0).toLocaleString();

  return (
    <div className="space-y-6" data-testid="accounting-journal-page">
      <PageHeader icon={ClipboardList} title="Accounting Journal" subtitle="Posted fees, receipts and waiver journal entries" />
      <Card className="p-3 flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs">From</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="jrn-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="jrn-to" /></div>
        <div><Label className="text-xs">Type</Label>
          <Select value={f.entry_kind || 'all'} onValueChange={(v) => setF({ ...f, entry_kind: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-40" data-testid="jrn-kind"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem><SelectItem value="invoice">Invoice</SelectItem><SelectItem value="receipt">Receipt</SelectItem><SelectItem value="waiver">Waiver</SelectItem></SelectContent></Select></div>
        <div><Label className="text-xs">Class</Label>
          <Select value={f.class_id || 'all'} onValueChange={(v) => setF({ ...f, class_id: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-48" data-testid="jrn-class"><SelectValue placeholder="Class" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All classes</SelectItem>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
      </Card>
      {loading ? <LoadingState /> : rows.length === 0 ? <EmptyState icon={ClipboardList} title="No journal entries" description="Post an invoice or receipt to see entries here" /> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="p-3">Date</th><th className="p-3">Reference</th><th className="p-3">Type</th><th className="p-3">Debit acct</th><th className="p-3">Credit acct</th><th className="p-3 text-right">Amount</th><th className="p-3">Student</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b hover:bg-muted/40" data-testid={`jrn-row-${r.id}`}>
                  <td className="p-3">{r.date}</td>
                  <td className="p-3 font-mono text-xs">{r.reference}<div className="text-muted-foreground">{r.voucher_number}</div></td>
                  <td className="p-3"><Badge className={KIND_COLORS[r.entry_kind] || ''}>{r.entry_kind || r.type}</Badge></td>
                  <td className="p-3 font-mono text-xs">{(r.debit_accounts || []).join(', ')}</td>
                  <td className="p-3 font-mono text-xs">{(r.credit_accounts || []).join(', ')}</td>
                  <td className="p-3 text-right">{money(r.amount)}</td>
                  <td className="p-3">{r.student_name || '\u2014'}<div className="text-xs text-muted-foreground">{r.class_name || ''}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
